// Built-in landing-page starter templates. Each is a full PageContent + theme +
// the form fields its Form section asks for, modeled on a family of references
// from the Orbit swipe board. Pure data; the gallery clones one into a new page
// (createPage with starterForm(tpl) as the page's CRM form). Edit freely after
// creating.
import type { PageStarter, StarterFormField } from './types';
import { formAccentFor } from '../model/pages';
import { courseLaunch } from './course-launch';
import { salesLetter } from './sales-letter';
import { darkProgram } from './dark-program';
import { editorialCreator } from './editorial-creator';
import { boldCoaching } from './bold-coaching';
import { freeTrial } from './free-trial';
import { bookOffer } from './book-offer';
import { challengeWebinar } from './challenge-webinar';
import { warmCourse } from './warm-course';
import { leadMagnet } from './lead-magnet';
import { vslCall } from './vsl-call';

export type { PageStarter, StarterFormField, StarterCategory } from './types';
export { STARTER_CATEGORIES } from './types';

/** Gallery order: the most-used page types first. */
export const PAGE_STARTERS: PageStarter[] = [
  courseLaunch, leadMagnet, salesLetter, challengeWebinar, vslCall, bookOffer,
  freeTrial, darkProgram, editorialCreator, warmCourse, boldCoaching,
];

/** The standard keys a starter's form asks for, in order. */
export function starterFieldKeys(fields: StarterFormField[] | undefined): string[] {
  return (fields ?? []).map((f) => (typeof f === 'string' ? f : f.key)).filter(Boolean);
}

/** The contact screen's title when a starter names none. */
export const CONTACT_LABEL = 'Where should we reach you?';

/** One question of a CRM form draft (utils/crmForms.js cleanQuestions shape). */
export type StarterQuestion = {
  id: string;
  type: 'contact' | 'text';
  label: string;
  required: boolean;
  help?: string;
  fields?: { phone: boolean; company: boolean; title: boolean };
};

/** A CRM form's settings (utils/crmForms.js cleanSettings shape). */
export type StarterFormSettings = {
  intro_title: string;
  intro_body: string;
  start_label: string;
  thanks_title: string;
  thanks_body: string;
  dq_title: string;
  dq_body: string;
  calendar_id: null;
  accent: string;
  redirect_url: string;
};

/** What POST /api/pages takes as `form`: the server creates this CRM form as a
 *  draft, linked to the new page (it goes live when the page is published). */
export type StarterFormDraft = { questions: StarterQuestion[]; settings: StarterFormSettings };

/**
 * The CRM form a template's page gets: one contact screen (name and email always,
 * phone and company when the starter asks for them), carrying the starter's form
 * title and description. A CRM form saves the lead only after its last screen, so
 * nothing follows the contact screen (a starter's optional website field is left
 * out; staff can add questions in CRM > Forms). No welcome screen (pages open the
 * form straight on its first question anyway). Null for a starter without form
 * fields.
 *
 * CRM rules that differ from Orbit's form: the first name is always required,
 * and a phone number is required whenever the screen asks for one.
 */
export function starterForm(tpl: Pick<PageStarter, 'form' | 'formTitle' | 'formDescription' | 'theme'>): StarterFormDraft | null {
  const keys = starterFieldKeys(tpl.form);
  if (!keys.length) return null;
  const help = (tpl.formDescription ?? '').trim();
  const contact: StarterQuestion = {
    id: 'contact',
    type: 'contact',
    label: (tpl.formTitle ?? '').trim() || CONTACT_LABEL,
    required: true,
    ...(help ? { help } : {}),
    fields: { phone: keys.includes('phone'), company: keys.includes('company'), title: false },
  };
  const questions: StarterQuestion[] = [contact];
  return {
    questions,
    settings: {
      intro_title: '',
      intro_body: '',
      start_label: 'Start',
      thanks_title: 'Thanks, we got it.',
      thanks_body: 'We will be in touch shortly.',
      dq_title: 'Thanks for your interest.',
      dq_body: 'We will review your answers and reach out if we are a good fit.',
      calendar_id: null,
      accent: formAccentFor(tpl.theme),
      redirect_url: '',
    },
  };
}

/** What a gallery or template preview passes PageView as `previewForm`, so the
 *  Form section shows a still of the form this template will create (FormMock). */
export function starterPreviewForm(tpl: Pick<PageStarter, 'form' | 'formTitle' | 'formDescription'>): { title?: string; description?: string; fields: StarterFormField[] } | null {
  if (!starterFieldKeys(tpl.form).length) return null;
  // The still matches the form starterForm creates: its one contact screen.
  const onContactScreen = (f: StarterFormField) => (typeof f === 'string' ? f : f.key) !== 'website';
  return { title: tpl.formTitle, description: tpl.formDescription, fields: (tpl.form ?? []).filter(onContactScreen) };
}

/** A still for a saved template or any CRM form draft ({questions, settings},
 *  as POST /api/pages/templates stores it): its contact screen, title and help
 *  line, as `previewForm`. Null when it has no contact screen. */
export function previewFormFromDraft(draft: { questions?: unknown } | null | undefined): { title?: string; description?: string; fields: StarterFormField[] } | null {
  const list = Array.isArray(draft?.questions) ? (draft!.questions as unknown[]) : [];
  const contact = list.find((q): q is { type: 'contact'; label?: unknown; help?: unknown; fields?: { phone?: unknown; company?: unknown } } =>
    !!q && typeof q === 'object' && (q as { type?: unknown }).type === 'contact');
  if (!contact) return null;
  const fields: StarterFormField[] = ['full_name', 'email'];
  if (contact.fields?.phone !== false) fields.push('phone');
  if (contact.fields?.company) fields.push('company');
  return {
    title: typeof contact.label === 'string' ? contact.label : undefined,
    description: typeof contact.help === 'string' ? contact.help : undefined,
    fields,
  };
}
